/* (c) Alpas Made LLC. All rights reserved. */
/* Ring configurator runtime, v1.0.0. Vanilla JS, one instance per [data-ring-cfg]. */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function formatMoney(cents, format) {
    var value = (cents / 100);
    function withCommas(number, decimals) {
      var parts = number.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return decimals ? parts.join('.') : parts[0];
    }
    function withDots(number, decimals) {
      var parts = number.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return decimals ? parts.join(',') : parts[0];
    }
    return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, token) {
      switch (token) {
        case 'amount': return withCommas(value, 2);
        case 'amount_no_decimals': return withCommas(value, 0);
        case 'amount_with_comma_separator': return withDots(value, 2);
        case 'amount_no_decimals_with_comma_separator': return withDots(value, 0);
        default: return withCommas(value, 2);
      }
    });
  }

  function sanitizeEngraving(str) {
    return str.replace(/[<>&"'`]/g, '').slice(0, 15);
  }

  // Same XSS strip as the engraving; the note is rendered as a line item property
  // in the cart and on the order, and Liquid does not auto-escape property values.
  function sanitizeNote(str, max) {
    return str.replace(/[<>&"'`]/g, '').slice(0, max);
  }

  function caratKey(sliderValue) {
    return parseFloat(sliderValue).toFixed(1) + 'ct';
  }

  // Short, readable grouping id shown to staff on the order (e.g. MJ-A47C2F).
  function makeBundleId() {
    var rand = Math.random().toString(36).slice(2, 8).toUpperCase();
    return 'MJ-' + rand;
  }

  function RingConfigurator(root) {
    this.root = root;
    var dataEl = root.querySelector('[data-ring-cfg-data]');
    this.data = JSON.parse(dataEl.textContent);
    this.form = root.querySelector('form');
    this.linesEl = root.querySelector('[data-ring-cfg-lines]');
    this.money = this.data.moneyFormat;
    this.bundleId = makeBundleId();
    this.state = {
      stone: 'moissanite', shape: 'Round', carat: '0.5', purity: '14K',
      color: 'Yellow Gold', size: '', engraving: '', note: ''
    };
    this.displayedTotal = this.data.base;
    this.init();
  }

  RingConfigurator.prototype.init = function () {
    var self = this;
    // Disable the theme's single-item id input; we post items[] instead.
    var idInput = this.form.querySelector('input[name="id"]');
    if (idInput) { idInput.disabled = true; }

    this.bindRadioGroup('[data-stone]', 'stone');
    this.bindRadioGroup('[data-shape]', 'shape', 'shape');
    this.bindRadioGroup('[data-purity]', 'purity');
    this.bindRadioGroup('[data-color]', 'color', 'color');

    var slider = this.root.querySelector('[data-carat]');
    slider.addEventListener('input', function () { self.state.carat = slider.value; self.update(); });

    var size = this.root.querySelector('[data-size]');
    size.addEventListener('change', function () {
      self.state.size = size.value;
      if (size.value) { self.root.classList.remove('ring-cfg--size-error'); self.root.querySelector('[data-ring-cfg-error]').hidden = true; }
      self.syncLines();
    });

    this.bindEngraving();
    this.bindNote();
    this.bindQuoteCtas();
    this.bindToggles();
    this.bindSubmit();
    this.bindSticky();

    this.update();
    this.breakdownAutoExpanded = false;
  };

  RingConfigurator.prototype.bindRadioGroup = function (selector, key, echo) {
    var self = this;
    var nodes = Array.prototype.slice.call(this.root.querySelectorAll(selector));
    nodes.forEach(function (node, i) {
      node.addEventListener('click', function () { self.select(nodes, node, key, echo); });
      node.addEventListener('keydown', function (e) {
        var dir = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : (e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0);
        if (!dir) { return; }
        e.preventDefault();
        var next = nodes[(i + dir + nodes.length) % nodes.length];
        next.focus();
        self.select(nodes, next, key, echo);
      });
    });
  };

  RingConfigurator.prototype.select = function (nodes, node, key, echo) {
    nodes.forEach(function (n) { n.classList.remove('is-selected'); n.setAttribute('aria-checked', 'false'); });
    node.classList.add('is-selected'); node.setAttribute('aria-checked', 'true');
    this.state[key] = node.getAttribute('data-' + key);
    if (echo) {
      var echoEl = this.root.querySelector('[data-echo="' + echo + '"]');
      if (echoEl) { echoEl.textContent = this.state[key]; }
    }
    this.update();
  };

  RingConfigurator.prototype.bindEngraving = function () {
    var self = this;
    var toggle = this.root.querySelector('[data-engrave-toggle]');
    var panel = this.root.querySelector('[data-engrave-panel]');
    var input = this.root.querySelector('[data-engrave-input]');
    var counter = this.root.querySelector('[data-engrave-counter]');
    toggle.addEventListener('click', function () {
      var open = panel.hidden;
      panel.hidden = !open;
      toggle.setAttribute('aria-expanded', String(open));
      toggle.textContent = open ? '- Remove free engraving' : '+ Add free engraving';
      if (!open) { input.value = ''; self.state.engraving = ''; counter.textContent = '0/15 characters, engraved inside the band, free'; self.syncLines(); }
    });
    input.addEventListener('input', function () {
      input.value = sanitizeEngraving(input.value);
      self.state.engraving = input.value;
      counter.textContent = input.value.length + '/15 characters, engraved inside the band, free';
      self.syncLines();
    });
  };

  RingConfigurator.prototype.bindNote = function () {
    var self = this;
    var input = this.root.querySelector('[data-note-input]');
    if (!input) { return; }
    var counter = this.root.querySelector('[data-note-counter]');
    var max = parseInt(input.getAttribute('maxlength'), 10) || 300;
    input.addEventListener('input', function () {
      var clean = sanitizeNote(input.value, max);
      if (clean !== input.value) { input.value = clean; }
      self.state.note = clean;
      if (counter) { counter.textContent = clean.length + '/' + max + ' characters'; }
      self.syncLines();
    });
  };

  RingConfigurator.prototype.bindQuoteCtas = function () {
    var self = this;
    ['[data-ring-cfg-quote-cta]', '[data-ring-cfg-bespoke]'].forEach(function (sel) {
      var el = self.root.querySelector(sel);
      if (!el) { return; }
      el.addEventListener('click', function (e) {
        // We always handle this ourselves: quote app, then on-page form, then URL.
        e.preventDefault();
        self.goToQuote();
      });
    });
  };

  RingConfigurator.prototype.bindToggles = function () {
    var self = this;
    var bd = this.root.querySelector('[data-breakdown-toggle]');
    var bdPanel = this.root.querySelector('[data-breakdown]');
    if (bd && bdPanel) {
      bd.addEventListener('click', function () {
        var open = bdPanel.hidden;
        bdPanel.hidden = !open;
        bd.setAttribute('aria-expanded', String(open));
        bd.textContent = open ? 'Hide breakdown' : 'View price breakdown';
      });
    }
    var sg = this.root.querySelector('[data-ring-cfg-sizeguide]');
    if (sg) {
      var findSizeGuideBtn = function () {
        // The theme's size-guide drawer is a product_drawer_with_tabs block; its
        // trigger uniquely has data-sidebar-id starting with product-information-sidebar-.
        var btns = Array.prototype.slice.call(document.querySelectorAll('[data-sidebar-button][data-sidebar-id^="product-information-sidebar-"]'));
        var sized = btns.filter(function (b) { return /size/i.test((b.getAttribute('aria-label') || '') + ' ' + (b.textContent || '')); });
        return sized[0] || btns[0] || null;
      };
      if (!findSizeGuideBtn()) {
        // No size-guide drawer on this product; hide our link rather than open the wrong drawer.
        sg.hidden = true;
      } else {
        sg.addEventListener('click', function () { var t = findSizeGuideBtn(); if (t) { t.click(); } });
      }
    }
  };

  RingConfigurator.prototype.pricing = function () {
    var s = this.state, d = this.data;
    var quoteMode = s.stone === 'natural';
    var stoneAdd = 0, centerId = null;
    if (s.stone === 'moissanite') {
      var mv = d.moissanite[caratKey(s.carat)];
      if (mv) { stoneAdd = mv.price; centerId = mv.id; }
    } else if (s.stone === 'labgrown') {
      var lv = d.labgrown[caratKey(s.carat)];
      if (lv) { stoneAdd = lv.price; centerId = lv.id; }
    }
    var metalAdd = s.purity === '18K' ? d.gold18k.price : 0;
    return { quoteMode: quoteMode, stoneAdd: stoneAdd, centerId: centerId, metalAdd: metalAdd, total: d.base + stoneAdd + metalAdd };
  };

  RingConfigurator.prototype.update = function () {
    var p = this.pricing();
    // slider fill
    var slider = this.root.querySelector('[data-carat]');
    var pct = ((parseFloat(slider.value) - 0.5) / (3.0 - 0.5)) * 100;
    slider.style.setProperty('--ring-cfg-fill', pct + '%');
    // carat echo
    var caratEcho = this.root.querySelector('[data-echo="carat"]');
    if (caratEcho) {
      // amount optional via ring_cfg_show_carat_price
      if (p.stoneAdd > 0) {
        caratEcho.textContent = this.data.showCaratPrice ? (parseFloat(slider.value).toFixed(1) + ' ct, +' + formatMoney(p.stoneAdd, this.money)) : (parseFloat(slider.value).toFixed(1) + ' ct');
      } else {
        caratEcho.textContent = parseFloat(slider.value).toFixed(1) + ' ct, included';
      }
    }
    // purity sub echo (amount optional via ring_cfg_show_purity_price; static "Upgrade" otherwise)
    var puritySub = this.root.querySelector('[data-echo="purity-sub"]');
    if (puritySub && this.data.showPurityPrice) { puritySub.textContent = '+' + formatMoney(this.data.gold18k.price, this.money); }

    this.root.classList.toggle('ring-cfg--quote', p.quoteMode);
    var quoteCard = this.root.querySelector('[data-ring-cfg-quote]');
    quoteCard.hidden = !p.quoteMode;
    // Lab diamond quality note: visible only while Lab Grown Diamond is selected.
    var labNote = this.root.querySelector('[data-labgrown-note]');
    if (labNote) { labNote.hidden = this.state.stone !== 'labgrown'; }
    // Pre-load the quote app's lazy chunk as soon as quote mode is entered.
    if (p.quoteMode && !this.quoteApiWarmed) { this.quoteApiWarmed = true; this.ensureQuoteApi(); }

    if (!p.quoteMode) {
      this.animateTotal(p.total);
      this.renderBreakdown(p);
    }
    this.updateSticky(p);
    this.updateQuoteLinks();
    this.updateHeaderPrice(p);
    this.syncLines();
  };

  RingConfigurator.prototype.updateHeaderPrice = function (p) {
    // Mirror the live total in the theme's price block under the product title.
    if (this.headerPriceEl === undefined) {
      this.headerPriceEl = document.querySelector('[data-product-block-container] .product-price__current-price')
        || document.querySelector('.product-price-wrapper .product-price__current-price');
    }
    if (!this.headerPriceEl) { return; }
    this.headerPriceEl.textContent = p.quoteMode ? 'Quoted individually' : formatMoney(p.total, this.money);
  };

  RingConfigurator.prototype.animateTotal = function (target) {
    var el = this.root.querySelector('[data-total]');
    var self = this;
    var start = this.displayedTotal;
    // Set the correct value synchronously first, the count-up below is a
    // best-effort flourish. The price must never depend on rAF firing.
    this.displayedTotal = target;
    el.textContent = formatMoney(target, this.money);
    el.classList.add('is-flash');
    setTimeout(function () { el.classList.remove('is-flash'); }, 250);
    if (reduceMotion || start === target) { return; }
    var delta = target - start, t0 = null;
    function step(ts) {
      if (!t0) { t0 = ts; }
      var k = Math.min((ts - t0) / 150, 1);
      if (k < 1) { el.textContent = formatMoney(Math.round(start + delta * k), self.money); requestAnimationFrame(step); }
      else { el.textContent = formatMoney(target, self.money); }
    }
    requestAnimationFrame(step);
  };

  RingConfigurator.prototype.renderBreakdown = function (p) {
    // breakdown markup is optional (ring_cfg_show_breakdown); total and deltas render elsewhere
    if (!this.root.querySelector('[data-breakdown]')) { return; }
    this.root.querySelector('[data-break-setting]').textContent = formatMoney(this.data.base, this.money);
    var stoneRow = this.root.querySelector('[data-break-stone-row]');
    if (p.stoneAdd > 0) {
      stoneRow.hidden = false;
      var label = (this.state.stone === 'labgrown' ? 'Lab Grown Diamond' : 'Moissanite') + ', ' + parseFloat(this.state.carat).toFixed(1) + 'ct';
      this.root.querySelector('[data-break-stone-label]').textContent = label;
      this.root.querySelector('[data-break-stone]').textContent = '+' + formatMoney(p.stoneAdd, this.money);
    } else { stoneRow.hidden = true; }
    var goldRow = this.root.querySelector('[data-break-gold-row]');
    if (p.metalAdd > 0) { goldRow.hidden = false; this.root.querySelector('[data-break-gold]').textContent = '+' + formatMoney(p.metalAdd, this.money); }
    else { goldRow.hidden = true; }
    // auto-expand once on first price change
    if (!this.breakdownAutoExpanded && (p.stoneAdd > 0 || p.metalAdd > 0)) {
      this.breakdownAutoExpanded = true;
      var bdPanel = this.root.querySelector('[data-breakdown]');
      var bd = this.root.querySelector('[data-breakdown-toggle]');
      bdPanel.hidden = false; bd.setAttribute('aria-expanded', 'true'); bd.textContent = 'Hide breakdown';
    }
  };

  RingConfigurator.prototype.updateSticky = function (p) {
    var total = this.root.querySelector('[data-sticky-total]');
    var btn = this.root.querySelector('[data-ring-cfg-sticky-btn]');
    if (p.quoteMode) { total.textContent = 'Quoted individually'; btn.textContent = 'Request a Quote'; }
    else { total.textContent = formatMoney(p.total, this.money); btn.textContent = 'Add to cart'; }
  };

  RingConfigurator.prototype.serializeConfig = function () {
    var s = this.state;
    var params = new URLSearchParams({
      stone: s.stone, shape: s.shape, carat: s.carat, purity: s.purity,
      color: s.color, size: s.size, engraving: s.engraving,
      product: (document.querySelector('link[rel="canonical"]') || {}).href || location.href
    });
    return params.toString();
  };

  // Human-readable summary of the current configuration, for the quote form.
  RingConfigurator.prototype.configSummary = function () {
    var s = this.state, d = this.data;
    var stoneLabel = s.stone === 'labgrown' ? 'Lab Grown Diamond' : (s.stone === 'natural' ? 'Natural Diamond' : 'Moissanite');
    var rows = [
      ['Stone type', stoneLabel],
      ['Stone shape', s.shape],
      ['Carat', parseFloat(s.carat).toFixed(1) + 'ct'],
      ['Metal purity', s.purity],
      ['Metal color', s.color],
      ['Ring size', s.size],
      ['Engraving', s.engraving],
      ['Customization note', s.note]
    ];
    var lines = ['RING CONFIGURATION'];
    rows.forEach(function (r) { if (r[1]) { lines.push(r[0] + ': ' + r[1]); } });
    if (d.productTitle) { lines.push('Design: ' + d.productTitle); }
    if (d.productUrl) { lines.push('Product link: ' + d.productUrl); }
    return lines.join('\n');
  };

  // Pre-fill the SA Quote (GRFQ) form already on the page. Targets only `message`
  // (Additional Notes), a stable built-in field name; the app's custom fields have
  // generated names that change whenever the merchant edits the form.
  RingConfigurator.prototype.fillQuoteForm = function () {
    var ta = document.querySelector('textarea[name="message"]');
    if (!ta) { return false; }
    var summary = this.configSummary();
    var existing = (ta.value || '').trim();
    // Keep anything the customer already typed; replace a previous summary rather than stacking.
    if (existing.indexOf('RING CONFIGURATION') === 0) {
      var afterPrev = existing.split(/\n\s*\n/).slice(1).join('\n\n').trim();
      existing = afterPrev;
    }
    var value = existing ? (summary + '\n\n' + existing) : summary;
    try {
      var setter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(ta), 'value').set;
      setter.call(ta, value);
    } catch (e) { ta.value = value; }
    ta.dispatchEvent(new Event('input', { bubbles: true }));
    ta.dispatchEvent(new Event('change', { bubbles: true }));

    // The form is hidden until requested (see the .is-quote-open rule).
    this.root.classList.add('is-quote-open');
    var form = ta.closest('form') || ta.parentElement;
    (form || ta).scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    // Focus the first empty contact field so the customer knows what to do next.
    var first = document.querySelector('input[name="contact_info[first_name]"]');
    if (first && !first.value) { setTimeout(function () { first.focus({ preventScroll: true }); }, reduceMotion ? 0 : 450); }
    return true;
  };

  // Config as {label: value} pairs, used for the quote item's line properties.
  RingConfigurator.prototype.configPairs = function () {
    var s = this.state;
    var stoneLabel = s.stone === 'labgrown' ? 'Lab Grown Diamond' : (s.stone === 'natural' ? 'Natural Diamond' : 'Moissanite');
    var pairs = [
      ['Stone type', stoneLabel],
      ['Stone shape', s.shape],
      ['Carat', parseFloat(s.carat).toFixed(1) + 'ct'],
      ['Metal purity', s.purity],
      ['Metal color', s.color],
      ['Ring size', s.size],
      ['Engraving', s.engraving],
      ['Customization note', s.note]
    ];
    return pairs
      .map(function (p) { return [p[0], p[1] == null ? '' : String(p[1]).trim()]; })
      .filter(function (p) { return p[1] !== ''; });
  };

  // Preferred path: hand the ring to the store's SA Quote (GRFQ) app with the full
  // configuration as line properties, which opens the app's own quote popup showing
  // every selection. Uses the app's documented-by-inspection entry point; wrapped so
  // any app change degrades to the form-fill / URL fallbacks instead of breaking.
  // GRFQApp.addToQuote is attached lazily (the app code-splits it); addProduct is the
  // public wrapper that loads the chunk and Object.assigns the real methods. Warm it up
  // once so the first click is not a no-op. The throwaway call is expected to fail.
  RingConfigurator.prototype.ensureQuoteApi = function () {
    var app = window.GRFQApp;
    if (!app) { return Promise.resolve(false); }
    if (typeof app.addToQuote === 'function') { return Promise.resolve(true); }
    var done = function () { return typeof app.addToQuote === 'function'; };
    try {
      return Promise.resolve(app.addProduct(document.createElement('button'), null)).then(done, done);
    } catch (e) { return Promise.resolve(done()); }
  };

  // Add one quote line via the app. `product` must be a Shopify product object.
  RingConfigurator.prototype.quoteLine = function (product, variantId, props) {
    var app = window.GRFQApp, cfg = window.GRFQConfigs;
    var form = document.createElement('form');
    form.setAttribute('action', '/cart/add');
    form.style.display = 'none';
    function hidden(name, value) {
      var i = document.createElement('input');
      i.type = 'hidden'; i.name = name; i.value = value;
      form.appendChild(i);
    }
    hidden('id', variantId);
    hidden('quantity', '1');
    (props || []).forEach(function (p) { hidden('properties[' + p[0] + ']', p[1]); });
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = (cfg.classes && cfg.classes.rfqButton) ? cfg.classes.rfqButton : 'rfq-btn';
    form.appendChild(btn);
    document.body.appendChild(form);
    var done = function () { if (form.parentNode) { form.parentNode.removeChild(form); } };
    var res = app.addToQuote(btn, product.id, product);
    return Promise.resolve(res).then(done, done);
  };

  // The priced add-ons that apply to the current selection, so the quote reflects
  // the real baseline (e.g. an 18K upgrade) instead of just the setting price.
  // Natural Diamond has no stone add-on by design; the specialist prices the stone.
  RingConfigurator.prototype.quoteAddons = function () {
    var p = this.pricing(), d = this.data, list = [];
    var handles = d.handles || {};
    if (p.centerId) {
      list.push({ handle: this.state.stone === 'labgrown' ? handles.labgrown : handles.moissanite, variantId: p.centerId });
    }
    if (p.metalAdd > 0 && handles.gold18k) {
      list.push({ handle: handles.gold18k, variantId: d.gold18k.id });
    }
    return list.filter(function (a) { return a.handle && a.variantId; });
  };

  RingConfigurator.prototype.addToQuoteApp = function () {
    var app = window.GRFQApp, cfg = window.GRFQConfigs;
    if (!app || !cfg || typeof app.addToQuote !== 'function') { return false; }
    var product = cfg.product;
    if (!product || !product.variants || !product.variants.length) { return false; }
    try {
      // Drop any previous quote entry for this ring so re-clicking replaces it
      // instead of incrementing the quantity.
      var groupKey = localStorage.getItem(cfg.shop_url + '/grfq/defaultGroupKey') || '0';
      var group = app.productGroup && app.productGroup.groups ? app.productGroup.groups[groupKey] : null;
      // Clear this ring AND its add-on lines, so re-clicking replaces the quote
      // instead of stacking quantities. Other products in the quote are left alone.
      var ourIds = [String(product.id)].concat((this.data.addonProductIds || []).map(String));
      if (group && group.items && group.items.length) {
        group.items.filter(function (it) { return ourIds.indexOf(String(it.product_id)) > -1; })
          .forEach(function (it) {
            if (typeof app.removeItemFormLocalStorage === 'function' && it.quoteId != null) {
              app.removeItemFormLocalStorage(groupKey, it.quoteId);
            }
          });
        group.items = group.items.filter(function (it) { return ourIds.indexOf(String(it.product_id)) < 0; });
      }

      // Ring line first (carries the full configuration), then each priced add-on so
      // the quote total reflects the real baseline.
      var self = this;
      var addons = this.quoteAddons();
      this.quoteLine(product, this.data.ringVariantId, this.configPairs())
        .then(function () {
          return addons.reduce(function (chain, a) {
            return chain.then(function () {
              return fetch('/products/' + a.handle + '.js', { headers: { Accept: 'application/json' } })
                .then(function (r) { return r.ok ? r.json() : null; })
                .then(function (prod) { if (prod) { return self.quoteLine(prod, a.variantId, null); } });
            });
          }, Promise.resolve());
        })
        .catch(function (e) { console.error('[RingConfigurator] add-on quote lines failed:', e); });
      this.fillQuoteNoteWhenReady();
      return true;
    } catch (e) {
      console.error('[RingConfigurator] quote app handoff failed, falling back:', e);
      return false;
    }
  };

  // The quote popup renders its contact form lazily (step 2), so watch for the
  // Additional Notes field and drop the customer's group 07 note into it once it
  // appears. Never overwrites text the customer has already typed there.
  RingConfigurator.prototype.fillQuoteNoteWhenReady = function () {
    var note = (this.state.note || '').trim();
    if (!note) { return; }
    var deadline = Date.now() + 30000;
    var iv = null;
    function attempt() {
      var fields = Array.prototype.slice.call(document.querySelectorAll('textarea[name="message"]'))
        .filter(function (t) { return t.offsetParent !== null; });
      var ta = fields[0];
      if (ta) {
        if (!(ta.value || '').trim()) {
          try {
            var setter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(ta), 'value').set;
            setter.call(ta, note);
          } catch (e) { ta.value = note; }
          ta.dispatchEvent(new Event('input', { bubbles: true }));
          ta.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return true;
      }
      return Date.now() > deadline;
    }
    if (attempt()) { return; }
    iv = setInterval(function () { if (attempt()) { clearInterval(iv); } }, 400);
  };

  // Quote CTA: hand off to the quote app, else pre-fill an on-page quote form,
  // else fall back to the configured quote URL.
  // Preference order (Reg 2026-07-28): use the store's existing inline
  // "Request Quotation Form" on the product page when it is there, since that is the
  // form the team already works with. Only fall back to the app's popup, then a URL.
  RingConfigurator.prototype.goToQuote = function () {
    var self = this;
    if (this.fillQuoteForm()) { return; }
    this.ensureQuoteApi().then(function () {
      if (self.addToQuoteApp()) { return; }
      var cta = self.root.querySelector('[data-ring-cfg-quote-cta]');
      var href = cta ? cta.getAttribute('href') : '';
      if (href && href !== '#') { window.location.href = href; }
    });
  };

  RingConfigurator.prototype.updateQuoteLinks = function () {
    var url = this.data.quoteUrl;
    var href = url ? (url + (url.indexOf('?') > -1 ? '&' : '?') + this.serializeConfig()) : '#';
    var cta = this.root.querySelector('[data-ring-cfg-quote-cta]');
    var bespoke = this.root.querySelector('[data-ring-cfg-bespoke]');
    if (cta) { cta.setAttribute('href', href); }
    if (bespoke) { bespoke.setAttribute('href', href); }
  };

  RingConfigurator.prototype.addLine = function (index, variantId, extraProps) {
    var self = this;
    function add(name, value) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'items[' + index + ']' + name;
      input.value = value;
      self.linesEl.appendChild(input);
    }
    add('[id]', variantId);
    add('[properties][_bundleId]', this.bundleId);
    if (extraProps) {
      Object.keys(extraProps).forEach(function (k) {
        // Trim so whitespace-only input never becomes an empty property on the order.
        var v = extraProps[k] == null ? '' : String(extraProps[k]).trim();
        if (v !== '') { add('[properties][' + k + ']', v); }
      });
    }
  };

  RingConfigurator.prototype.syncLines = function () {
    while (this.linesEl.firstChild) { this.linesEl.removeChild(this.linesEl.firstChild); }
    var p = this.pricing();
    if (p.quoteMode) { return; }
    var s = this.state;
    // Order/packing-slip labels. Title Case, production wording, stable order.
    var ringProps = {
      'Center Stone': s.stone === 'labgrown' ? 'Lab Grown Diamond' : 'Moissanite',
      'Stone Shape': s.shape,
      'Carat Weight': parseFloat(s.carat).toFixed(1) + ' ct',
      'Metal Purity': s.purity,
      'Metal Color': s.color,
      'Ring Size': s.size,
      'Engraving': s.engraving,
      'Special Request': s.note
    };
    var idx = 0;
    this.addLine(idx++, this.data.ringVariantId, ringProps);
    if (p.centerId) { this.addLine(idx++, p.centerId, null); }
    if (p.metalAdd > 0) { this.addLine(idx++, this.data.gold18k.id, null); }
  };

  RingConfigurator.prototype.bindSubmit = function () {
    var self = this;
    // Capture phase: run before the theme's product-form submit handler.
    this.form.addEventListener('submit', function (e) {
      var p = self.pricing();
      if (p.quoteMode) { e.preventDefault(); e.stopImmediatePropagation(); self.goToQuote(); return; }
      if (!self.state.size) {
        e.preventDefault(); e.stopImmediatePropagation();
        self.root.classList.add('ring-cfg--size-error');
        var err = self.root.querySelector('[data-ring-cfg-error]');
        err.hidden = false;
        var sizeEl = self.root.querySelector('[data-size]');
        sizeEl.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        sizeEl.focus();
        return;
      }
      // fresh bundle id per add, stamp all current lines
      self.bundleId = makeBundleId();
      self.syncLines();
    }, true);
  };

  RingConfigurator.prototype.bindSticky = function () {
    var self = this;
    var sticky = this.root.querySelector('[data-ring-cfg-sticky]');
    var panel = this.root.querySelector('[data-ring-cfg-price-panel]');
    var btn = this.root.querySelector('[data-ring-cfg-sticky-btn]');
    btn.addEventListener('click', function () {
      if (self.pricing().quoteMode) { self.goToQuote(); return; }
      var atc = self.root.querySelector('[data-ring-cfg-atc]');
      if (atc) { atc.click(); }
    });
    if (!('IntersectionObserver' in window)) { return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) { sticky.hidden = false; sticky.classList.toggle('is-visible', !entry.isIntersecting); });
    }, { threshold: 0 });
    io.observe(panel);
  };

  function boot() {
    document.querySelectorAll('[data-ring-cfg]').forEach(function (el) {
      if (el.__ringCfgInit) { return; }
      el.__ringCfgInit = true;
      try { new RingConfigurator(el); } catch (err) { console.error('[RingConfigurator] init failed:', err); }
    });
  }
  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', boot); } else { boot(); }
})();
